import {
  ActionIcon,
  Button,
  Checkbox,
  Input,
  JsonInput,
  Select,
  Text,
  TextInput,
  UnstyledButton
} from "@marketplace/ui";
import { Controller, useFieldArray, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdAddCircleOutline,
  MdDeleteSweep,
  MdOutlineDeleteForever
} from "react-icons/md";

import type { AddProductFormSchema } from "../../hooks/schemas/use-add-product-form-schema";
import type { EditProductFormSchema } from "../../hooks/schemas/use-edit-product-form-schema";

type ProductFormApiPackageProps = {
  formMethods: UseFormReturn<AddProductFormSchema | EditProductFormSchema>;
};

const HTTP_METHODS = [
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
  { label: "PUT", value: "PUT" },
  { label: "PATCH", value: "PATCH" },
  { label: "DELETE", value: "DELETE" }
];

const BODY_TYPES = [
  { label: "int", value: "int" },
  { label: "string", value: "string" },
  { label: "bool", value: "bool" },
  { label: "varchar", value: "varchar" }
];

type ApiBodyProps = {
  apiIndex: number;
  control: any;
  register: any;
  errors: any;
  watch: any;
};

const ApiBodyFields: React.FC<ApiBodyProps> = ({
  apiIndex,
  control,
  register,
  errors,
  watch
}) => {
  const currentMethod = watch(`apiPackage.apis.${apiIndex}.method`);
  const { fields, append, remove } = useFieldArray({
    control,
    name: `apiPackage.apis.${apiIndex}.body` as const
  });

  return (
    <Input.Wrapper
      label="Body"
      error={
        ((errors.apiPackage as any)?.apis as any[])?.[apiIndex]?.body?.message
      }
    >
      <div className="flex flex-col gap-y-2">
        {fields.map((field, bodyIndex) => (
          <div key={field.id} className="flex items-center relative pr-6">
            <div className="grid grid-cols-2 gap-2 flex-1 mr-2">
              <TextInput
                {...register(
                  `apiPackage.apis.${apiIndex}.body.${bodyIndex}.key` as const
                )}
                placeholder="e.g., amount"
                error={
                  ((errors.apiPackage as any)?.apis as any[])?.[apiIndex]
                    ?.body?.[bodyIndex]?.key?.message
                }
              />
              <Controller
                name={
                  `apiPackage.apis.${apiIndex}.body.${bodyIndex}.type` as const
                }
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    placeholder="Choose type"
                    data={BODY_TYPES}
                    withAsterisk={currentMethod !== "GET"}
                    error={
                      ((errors.apiPackage as any)?.apis as any[])?.[apiIndex]
                        ?.body?.[bodyIndex]?.type?.message
                    }
                  />
                )}
              />
            </div>
            <div className="absolute w-6 h-full top-0 right-0 flex items-center">
              {bodyIndex === 0 && (
                <ActionIcon
                  variant="subtle"
                  onClick={() => append({ key: "", type: "" })}
                  className="text-noble-blue-500 hover:opacity-80"
                >
                  <MdAddCircleOutline className="h-5 w-5" />
                </ActionIcon>
              )}
              {fields.length > 1 && bodyIndex > 0 && (
                <ActionIcon
                  variant="subtle"
                  onClick={() => remove(bodyIndex)}
                  className="text-[#FF0606] hover:opacity-80"
                >
                  <MdDeleteSweep className="h-5 w-5" />
                </ActionIcon>
              )}
            </div>
          </div>
        ))}
      </div>
    </Input.Wrapper>
  );
};

export const ProductFormApiPackage: React.FC<ProductFormApiPackageProps> = ({
  formMethods
}) => {
  const { t: tProduct } = useTranslation("product");
  const { register, control, watch, formState } = formMethods;
  const { errors } = formState;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "apiPackage.apis"
  });

  const watchedMethod = watch("apiPackage.apis");

  const needsBody = (method: string) => {
    return ["POST", "PUT", "PATCH", "DELETE"].includes(method);
  };

  return (
    <>
      <div className="mb-3 flex justify-between items-center">
        <Text className="text-fjord-grey-500">
          {tProduct("addProductPage.form.legends.apiList")}
        </Text>
        <Controller
          name="showOnBuyer"
          control={control}
          render={({ field }) => (
            <Checkbox
              label={tProduct("addProductPage.form.labels.showOnBuyer")}
              classNames={{
                label: "text-h-xxs !font-bold"
              }}
              checked={field.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                field.onChange(e.currentTarget.checked)
              }
              error={(errors.showOnBuyer as any)?.message}
            />
          )}
        />
      </div>

      <div className="space-y-4 p-2 rounded-lg bg-mischka-100">
        {/* Host, Header, Key */}
        <div className="space-y-4">
          <TextInput
            {...register("apiPackage.host")}
            label="Host"
            placeholder="Your host"
            withAsterisk
            error={(errors.apiPackage as any)?.host?.message}
          />
          <TextInput
            {...register("apiPackage.header")}
            label="Header"
            placeholder="Your header"
            withAsterisk
            error={(errors.apiPackage as any)?.header?.message}
          />
          <TextInput
            {...register("apiPackage.key")}
            label="Key"
            placeholder="Your key"
            withAsterisk
            error={(errors.apiPackage as any)?.key?.message}
          />
        </div>

        <hr className="flex-1 h-px border-0 border-solid bg-mischka-400" />

        {/* API Endpoints */}
        <div className="space-y-4">
          {fields.map((field, index) => {
            const currentMethod = watchedMethod?.[index]?.method;
            const showBodyFields = currentMethod && needsBody(currentMethod);

            return (
              <>
                <div className="flex items-center justify-between gap-2">
                  <Text size="xl" className="text-fjord-grey-500">
                    API {index + 1}
                  </Text>
                  <hr className="flex-1 h-px border-0 border-solid bg-mischka-400" />
                  {fields.length > 1 && (
                    <UnstyledButton
                      onClick={() => remove(index)}
                      className="flex items-center gap-x-1 text-[#FF0606] hover:opacity-80"
                      title={tProduct("buttonTitle.removeApi")}
                    >
                      <Text size="xl">delete</Text>
                      <MdOutlineDeleteForever className="h-6 w-6" />
                    </UnstyledButton>
                  )}
                </div>
                <div key={field.id} className="space-y-4">
                  <TextInput
                    {...register(`apiPackage.apis.${index}.name` as const)}
                    label="API Name"
                    placeholder="Your api name"
                    withAsterisk
                    error={
                      ((errors.apiPackage as any)?.apis as any[])?.[index]?.name
                        ?.message
                    }
                  />
                  <TextInput
                    {...register(`apiPackage.apis.${index}.endpoint` as const)}
                    label="Endpoint"
                    placeholder="Your endpoint"
                    withAsterisk
                    error={
                      ((errors.apiPackage as any)?.apis as any[])?.[index]
                        ?.endpoint?.message
                    }
                  />
                  <Controller
                    name={`apiPackage.apis.${index}.method` as const}
                    control={control}
                    render={({ field: methodField }) => (
                      <Select
                        {...methodField}
                        label="Method"
                        placeholder="Choose method"
                        data={HTTP_METHODS}
                        withAsterisk
                        error={
                          ((errors.apiPackage as any)?.apis as any[])?.[index]
                            ?.method?.message
                        }
                      />
                    )}
                  />

                  {/* Body fields (show only for POST, PUT, PATCH, DELETE) */}
                  {showBodyFields && (
                    <ApiBodyFields
                      apiIndex={index}
                      control={control}
                      register={register}
                      errors={errors}
                      watch={watch}
                    />
                  )}

                  {/* Responses */}
                  <Controller
                    name={`apiPackage.apis.${index}.response` as const}
                    control={control}
                    render={({ field: responseField }) => (
                      <JsonInput
                        {...responseField}
                        label="Responses"
                        placeholder="Your responses"
                        withAsterisk
                        autosize
                        minRows={10}
                        maxRows={15}
                        error={
                          ((errors.apiPackage as any)?.apis as any[])?.[index]
                            ?.response?.message
                        }
                      />
                    )}
                  />
                </div>
              </>
            );
          })}
        </div>

        <Button
          fullWidth
          variant="secondary"
          size="sm"
          classNames={{
            label: "text-fjord-grey-500",
            section: "text-fjord-grey-500"
          }}
          onClick={() =>
            append({
              name: "",
              endpoint: "",
              method: "",
              body: [{ key: "", type: "" }],
              response: ""
            })
          }
          title={tProduct("buttonTitle.addPackage")}
          rightSection={<MdAdd size={14} />}
        >
          {tProduct("addProductPage.form.buttons.addPackage")}
        </Button>
      </div>
    </>
  );
};
